import { useEffect, useRef } from "react";

/*
 * The phone's back button closes the sheet, map or modal on top — as in a
 * native app — instead of leaving the screen under it. Back used to change
 * the page behind an open modal (which then floated over the wrong screen)
 * and left Home altogether from the address sheet.
 *
 * Each open layer adds one history entry (same URL, so the router stays put)
 * and sits on a stack; back pops the entry and closes only the top layer, so
 * a map opened over the address sheet closes first. A layer closed from its
 * own controls takes its entry back off, so back is not spent on it later.
 */

type Layer = { id: number; close: () => void };

const layers: Layer[] = [];
let nextId = 1;
/* Pops this module caused itself (removing a closed layer's entry). */
let ownPops = 0;
let listening = false;

const onPopState = () => {
  if (ownPops > 0) {
    ownPops -= 1;
    return;
  }
  layers.pop()?.close();
};

export default function useBackToClose(open: boolean, onClose: () => void) {
  const closeRef = useRef(onClose);
  closeRef.current = onClose;

  useEffect(() => {
    if (!open) return;
    if (!listening) {
      window.addEventListener("popstate", onPopState);
      listening = true;
    }

    const id = nextId++;
    const layer: Layer = { id, close: () => closeRef.current() };
    layers.push(layer);
    window.history.pushState(
      { ...window.history.state, ypLayer: id },
      "",
      window.location.href
    );

    return () => {
      const index = layers.indexOf(layer);
      if (index === -1) return; // closed by back: its entry is already gone
      layers.splice(index, 1);
      /* Closed from the layer itself. The top entry is removed only if it
         belongs to a layer that is no longer open — this one, or one whose
         removal raced a sheet opening right after (pickup → destination).
         If the close came with a navigation (Confirm that moves on to
         another screen), that screen's entry is on top and must not be
         popped. */
      const top = window.history.state?.ypLayer;
      if (top && !layers.some((open) => open.id === top)) {
        ownPops += 1;
        window.history.back();
      }
    };
  }, [open]);
}
